import React, { useState, useContext, useCallback, useEffect } from "react";
import {
  makeStyles,
  Grid,
  Typography,
  Card,
  List,
  ListItem,
} from "@material-ui/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { LangContext } from "../../Context/Langcontext";

import { getUsageStat } from "../../API/Usage";
import { ServiceContext } from "../../Context/ServiceContext";
import { FaCircle } from "react-icons/fa";

const useStyle = makeStyles((theme) => ({
  root: {
    width: "99.5%",
    height: "100%",
  },
  btnstyle: {
    borderRadius: 8,
    padding: "0.4rem 2rem",
    fontSize: "1rem",
    fontWeight: 200,
    textTransform: "unset",
    backgroundColor: "#E16733",
    color: "#FFFFFF",
    "&:hover": {
      background: "#e16733",
      boxShadow: `0px 0px 20px -1px #a8a8a8`,
      transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    },
  },
  maintext: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#595A5A",
  },
  secondtext: {
    fontSize: "1rem",
    fontWeight: 200,
    color: "#595A5A",
  },
  image: {
    width: "1.2rem",
    height: "auto",
    marginRight: "1rem",
  },
  detailtext: {
    fontSize: "1rem",
    fontWeight: 200,
    color: "#595A5A",
  },
  boldtext: {
    fontSize: "1rem",
    fontWeight: 700,
    color: "#595A5A",
  },
  showbar: {
    height: 450,
    // width: "100%",
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
      width: "100%",
    },
    [theme.breakpoints.up("md")]: {
      minWidth: "50%",
      maxWidth: "80%",
    },
  },
  cardapi: {
    width: "100%",
    backgroundColor: "#FFFFFF",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    flexDirection: "column",
    boxShadow: "1px 1px 5px 0px rgba(191,192,191,1)",
    WebkitBoxShadow: "1px 1px 5px 0px rgba(191,192,191,1)",
    MozBoxShadow: "1px 1px 5px 0px rgba(191,192,191,1)",
    borderRadius: 8,
    marginTop: "0.5rem",
    marginBottom: "0.1rem",
    minHeight: "calc(100vh - 17.9rem)",
    maxHeight: "100%",
  },
  cardtooltip: {
    margin: "1rem",
    display: "flex",
    width: "100%",
    alignItems: "center",
    flexDirection: "column",
    zIndex: 99999,
    backgroundColor: "#ffffff87",
    borderRadius: 8,
    boxShadow: "1px 1px 5px 0px rgba(191,192,191,1)",
    WebkitBoxShadow: "1px 1px 5px 0px rgba(191,192,191,1)",
    MozBoxShadow: "1px 1px 5px 0px rgba(191,192,191,1)",
  },
  fonttooltip: {
    fontSize: "0.7rem",
    fontWeight: 400,
  },
  fontlabel: {
    fontSize: "0.8rem",
    fontWeight: 700,
    color: "#595A5A",
  },
}));

const Summary = () => {
  const classes = useStyle();
  const langContext = useContext(LangContext);
  const lang = langContext.lang.data;
  const [sum, setSum] = useState([]);
  const [fetch, setFetch] = useState(false);
  const serviceContext = useContext(ServiceContext);

  const payload = [
    {
      title: "Data API",
      color: "#ADCAC8",
      id: "ID01",
    },
    { title: "Map API", color: "#F1B351", id: "ID02" },
  ];

  const findInDataApi = (id) => {
    let dataapi = serviceContext.data.find((rs) => {
      return rs.category === "data" && rs._id === id;
    });
    return dataapi;
  };

  const findInMapApi = (id) => {
    let dataapi = serviceContext.data.find((rs) => {
      return rs.category === "map" && rs._id === id;
    });

    return dataapi;
  };

  const getMonth = (date) => {
    const monthNames = [
      lang.January,
      lang.February,
      lang.March,
      lang.April,
      lang.May,
      lang.June,
      lang.July,
      lang.August,
      lang.September,
      lang.October,
      lang.November,
      lang.December,
    ];
    let dateObj = new Date(date);
    let month = monthNames[dateObj.getMonth()];

    return month;
  };

  const chart = sum
    .sort((a, b) => (a._id.datetime_ref > b._id.datetime_ref ? 1 : -1))
    .map((rs, index) => {
      let sumdata = 0;
      let summap = 0;
      return {
        name: getMonth(rs._id.datetime_ref),
        dataapi: findInDataApi(rs._id.service_id)
          ? (sumdata = Number(sumdata + Number(rs.sum_net)))
          : (sumdata = Number(sumdata + 0)),
        mapapi: findInMapApi(rs._id.service_id)
          ? (summap = Number(summap + Number(rs.sum_net)))
          : (summap = Number(summap + 0)),
      };
    });

  const mergeChart = (data) => {
    const output = [];

    data.forEach(function (item) {
      var existing = output.filter(function (v, i) {
        return v.name === item.name;
      });

      if (existing.length) {
        var existingIndex = output.indexOf(existing[0]);
        output[existingIndex].dataapi = Number(
          Number(
            Number(output[existingIndex].dataapi) + Number(item.dataapi)
          ).toFixed(2)
        );

        output[existingIndex].mapapi = Number(
          Number(
            Number(output[existingIndex].mapapi) + Number(item.mapapi)
          ).toFixed(2)
        );
      } else {
        if (
          typeof item.dataapi === "number" &&
          typeof item.mapapi === "number"
        ) {
          output.push(item);
        }
      }
    });

    return output;
  };

  const getSum = useCallback(async () => {
    const dt = await getUsageStat("/aggregate?group=service_id,datetime_ref");

    if (dt.status.code === 200 && dt.data.length !== 0) {
      setSum(dt.data);
    } else {
    }
  }, []);

  useEffect(() => {
    if (!fetch) {
      getSum();
      setFetch(true);
    } else {
    }
  }, [fetch, getSum]);

  const renderLegend = () => {
    return (
      <div
        style={{
          margin: "1rem",
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
        }}
      >
        {payload.map((entry, index) => (
          <div
            key={`item-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <FaCircle
              style={{
                color: entry.color,
                fontSize: "0.5rem",
                marginRight: "0.5rem",
                marginLeft: "0.5rem",
              }}
            />
            <Typography className={classes.secondtext}>
              {entry.title}
            </Typography>
          </div>
        ))}
      </div>
    );
  };

  const noData = [{ name: lang.NotFound }];
  const mergedObject = mergeChart(chart);

  const renderTooltip = (props) => {
    let { payload } = props;
    if (props.active) {
      return (
        <Card className={classes.cardtooltip}>
          <List>
            {payload.map((entry, index) => {
              return (
                <ListItem key={`${entry.dataKey}_${index}`}>
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <FaCircle
                        color={entry.color}
                        size={12}
                        style={{ margin: "0rem 0.5rem" }}
                      />
                      <Typography className={classes.fonttooltip}>
                        {`${
                          entry.dataKey === "dataapi" ? "Data API" : "Map API"
                        } : ${entry.value} ${lang.THB}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              );
            })}
          </List>
        </Card>
      );
    } else {
      return null;
    }
  };

  return (
    <div className={classes.root}>
      <Card className={classes.cardapi}>
        <div
          style={{
            width: "100%",
          }}
        >
          <div style={{ margin: "2rem" }}>
            <Grid container spacing={0}>
              <Grid item md={12} sm={12} xs={12}>
                <Typography className={classes.maintext}>
                  {lang.Monthlyexpendituresummary}
                </Typography>
                <Typography className={classes.secondtext}>
                  {lang.SubtitlesSummary}
                </Typography>
              </Grid>

              <Grid
                item
                md={12}
                sm={12}
                xs={12}
                style={{ display: "flex", justifyContent: "center" }}
              >
                <div className={classes.showbar}>
                  <ResponsiveContainer width="99%">
                    <BarChart
                      data={mergedObject.length ? mergedObject : noData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                      barSize={mergedObject.length < 3 ? 50 : 30}
                    >
                      <CartesianGrid vertical={false} strokeLinecap="round" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip content={renderTooltip} />
                      <Legend content={renderLegend} verticalAlign="top" />
                      <Bar dataKey="dataapi" fill="#ADCAC8" label="Data API" />
                      <Bar dataKey="mapapi" fill="#F1B351" label="Map API" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Summary;
